import { Button, Card, CardContent } from "@roberttamas/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Check, Loader2 } from "@/components/icons";
import { StepFooter, StepHeading } from "@/components/setup/step-chrome";
import type { SetupStepId } from "@/components/setup/steps";
import { cn } from "@/lib/utils";

/**
 * Shared chrome for the optional-feature steps (menu, timeline, DJ):
 * the step's heading with two tappable Yes/No cards; choosing Yes
 * reveals the guided mini-editor passed as children. The footer's
 * primary button is owned by the parent step (it knows what saving
 * means).
 */
export function YesNoStep({
  stepId,
  yesLabel,
  noLabel,
  enabled,
  onChoose,
  onBack,
  footer,
  children,
}: {
  stepId: SetupStepId;
  yesLabel: string;
  noLabel: string;
  enabled: boolean | null;
  onChoose: (value: boolean) => void;
  onBack: (() => void) | null;
  footer: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <StepHeading step={stepId} />

        <div className="grid gap-3 sm:grid-cols-2">
          <ChoiceCard
            label={yesLabel}
            active={enabled === true}
            onClick={() => onChoose(true)}
          />
          <ChoiceCard
            label={noLabel}
            active={enabled === false}
            onClick={() => onChoose(false)}
          />
        </div>

        {enabled === true && children}

        <StepFooter onBack={onBack}>{footer}</StepFooter>
      </CardContent>
    </Card>
  );
}

function ChoiceCard({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "flex items-center justify-center gap-2 rounded-lg border p-5 text-base font-medium transition-colors",
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "hover:bg-accent/40",
      )}
    >
      {active && <Check className="h-4 w-4 text-primary" />}
      {label}
    </button>
  );
}

/** The standard primary button for a feature step's footer. */
export function StepSaveButton({
  saving,
  disabled,
  linear,
  onClick,
}: {
  saving: boolean;
  disabled?: boolean;
  linear: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Button type="button" size="lg" onClick={onClick} disabled={saving || disabled}>
      {saving ? (
        <>
          <Loader2 className="animate-spin" /> {t("common.saving")}
        </>
      ) : linear ? (
        t("setup.saveContinue")
      ) : (
        t("common.save")
      )}
    </Button>
  );
}
